import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import { Star, MessageSquare, Star as StarIcon, RefreshCw, Lock, Check, KeyRound, Filter, Settings, Plus, Pencil, Trash2, X, Send } from "lucide-react";
import { LangToggle, fmtTime, ChatBubble } from "../components/Shared";
import { translations } from "@/i18n";
import { getAudioContext, playChime } from "../lib/chime";
import { formatRupiah } from "../data";

const STAFF_NAME_KEY = "gt_staff_name";
const POLL_MS = 15000;
const THREAD_POLL_MS = 5000;

const getStaffName = (prompt) => {
  let name = localStorage.getItem(STAFF_NAME_KEY);
  if (!name) {
    name = (window.prompt(prompt) || "Staff").trim() || "Staff";
    localStorage.setItem(STAFF_NAME_KEY, name);
  }
  return name;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiFetch = (path, init = {}) =>
  fetch(`${API}${path}`, {
    credentials: "include",
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) + " · " + fmtTime(iso);
  } catch {
    return "";
  }
};

const EMPTY_PROMO_FORM = { emoji: "", title_id: "", title_en: "", desc_id: "", desc_en: "", active: true, order: 0 };
const EMPTY_MENU_FORM = { category: "food", name: "", price: 0, desc_id: "", desc_en: "", active: true, order: 0 };

export default function StaffDashboard({ lang, setLang }) {
  const T = translations[lang];
  const [authed, setAuthed] = useState(null); // null = checking
  const [passcode, setPasscode] = useState("");
  const [gateError, setGateError] = useState("");
  const [gateLoading, setGateLoading] = useState(false);

  const [tab, setTab] = useState("feedback");
  const [feedback, setFeedback] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnresolvedOnly, setShowUnresolvedOnly] = useState(false);

  const seenIdsRef = useRef(null); // null = baseline not established yet

  // Check existing session on mount
  useEffect(() => {
    apiFetch("/staff/status")
      .then((r) => setAuthed(r.ok))
      .catch(() => setAuthed(false));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fb, rq] = await Promise.all([
        apiFetch("/feedback").then((r) => (r.ok ? r.json() : [])),
        apiFetch("/staff/requests").then((r) => (r.ok ? r.json() : [])),
      ]);
      setFeedback(Array.isArray(fb) ? fb : []);
      setRequests(Array.isArray(rq) ? rq : []);
      seenIdsRef.current = new Set((Array.isArray(rq) ? rq : []).map((r) => r.id));
    } catch {
      setFeedback([]);
      setRequests([]);
    }
    setLoading(false);
  }, []);

  // Poll for new requests so staff get notified without a manual refresh.
  const poll = useCallback(async () => {
    try {
      const rq = await apiFetch("/staff/requests").then((r) => (r.ok ? r.json() : null));
      if (!Array.isArray(rq)) return;
      if (seenIdsRef.current) {
        const isNew = rq.some((r) => !r.resolved && !seenIdsRef.current.has(r.id));
        if (isNew) playChime();
      }
      seenIdsRef.current = new Set(rq.map((r) => r.id));
      setRequests(rq);
    } catch {
      // silent — next poll will retry
    }
  }, []);

  useEffect(() => {
    if (authed === true) load();
  }, [authed, load]);

  useEffect(() => {
    if (authed !== true) return;
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [authed, poll]);

  const unlock = async (e) => {
    e.preventDefault();
    setGateError("");
    setGateLoading(true);
    try {
      const r = await apiFetch("/staff/verify", { method: "POST", body: JSON.stringify({ passcode }) });
      setPasscode("");
      if (r.ok) {
        getAudioContext(); // prime on this real user gesture so later polls can play a chime
        setAuthed(true);
      } else setGateError(T.staffGateError);
    } catch {
      setGateError(T.staffGateError);
    }
    setGateLoading(false);
  };

  const logout = async () => {
    try {
      await apiFetch("/staff/logout", { method: "POST" });
    } catch {}
    setAuthed(false);
  };

  const toggleResolve = async (r) => {
    const newVal = !r.resolved;
    const resolvedBy = newVal ? getStaffName(T.staffNamePrompt) : null;
    const resolvedAt = newVal ? new Date().toISOString() : null;
    const prev = { resolved: r.resolved, resolved_by: r.resolved_by, resolved_at: r.resolved_at };
    setRequests((cur) =>
      cur.map((x) => (x.id === r.id ? { ...x, resolved: newVal, resolved_by: resolvedBy, resolved_at: resolvedAt } : x))
    );
    try {
      await apiFetch(`/staff/requests/${r.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ resolved: newVal, resolved_by: resolvedBy }),
      });
    } catch {
      setRequests((cur) => cur.map((x) => (x.id === r.id ? { ...x, ...prev } : x)));
    }
  };

  const visibleRequests = showUnresolvedOnly ? requests.filter((r) => !r.resolved) : requests;

  // Group individual guest messages into one conversation thread per session_id.
  const threads = useMemo(() => {
    const map = new Map();
    for (const r of visibleRequests) {
      if (!map.has(r.session_id)) map.set(r.session_id, []);
      map.get(r.session_id).push(r);
    }
    return Array.from(map.entries())
      .map(([sessionId, msgs]) => {
        const sorted = [...msgs].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        const latest = sorted[sorted.length - 1];
        return {
          sessionId,
          guestName: latest.guest_name,
          roomNumber: latest.room_number,
          latest,
          unresolvedCount: msgs.filter((m) => !m.resolved).length,
          messageIds: msgs.map((m) => m.id),
        };
      })
      .sort((a, b) => new Date(b.latest.timestamp) - new Date(a.latest.timestamp));
  }, [visibleRequests]);

  // ===== Conversation thread (reply) =====
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyInput, setReplyInput] = useState("");
  const [replySending, setReplySending] = useState(false);

  const fetchThread = useCallback(async (sessionId) => {
    try {
      const res = await fetch(`${API}/operator/history/${sessionId}`);
      const hist = await res.json();
      setThreadMessages(Array.isArray(hist) ? hist : []);
    } catch {
      // keep whatever is already shown; next poll will retry
    }
  }, []);

  const toggleThread = async (t) => {
    if (expandedSessionId === t.sessionId) {
      setExpandedSessionId(null);
      return;
    }
    setExpandedSessionId(t.sessionId);
    setReplyInput("");
    setThreadLoading(true);
    await fetchThread(t.sessionId);
    setThreadLoading(false);
  };

  useEffect(() => {
    if (!expandedSessionId) return;
    const id = setInterval(() => fetchThread(expandedSessionId), THREAD_POLL_MS);
    return () => clearInterval(id);
  }, [expandedSessionId, fetchThread]);

  const sendReply = async (t) => {
    const text = replyInput.trim();
    if (!text || replySending) return;
    setReplySending(true);
    try {
      await apiFetch("/staff/operator/reply", {
        method: "POST",
        body: JSON.stringify({ session_id: t.sessionId, guest_name: t.guestName, room_number: t.roomNumber, message: text }),
      });
      setReplyInput("");
      await fetchThread(t.sessionId);
    } catch {
      // leave input as-is so staff can retry
    }
    setReplySending(false);
  };

  const setThreadResolved = async (t, newVal) => {
    const resolvedBy = newVal ? getStaffName(T.staffNamePrompt) : null;
    const resolvedAt = newVal ? new Date().toISOString() : null;
    const targetIds = t.messageIds.filter((id) => requests.find((r) => r.id === id)?.resolved !== newVal);
    setRequests((cur) =>
      cur.map((r) => (targetIds.includes(r.id) ? { ...r, resolved: newVal, resolved_by: resolvedBy, resolved_at: resolvedAt } : r))
    );
    try {
      await Promise.all(
        targetIds.map((id) => apiFetch(`/staff/requests/${id}/resolve`, { method: "POST", body: JSON.stringify({ resolved: newVal, resolved_by: resolvedBy }) }))
      );
    } catch {
      load();
    }
  };

  // ===== Content management (promo & menu) =====
  const [contentTab, setContentTab] = useState("promo");
  const [adminPromos, setAdminPromos] = useState([]);
  const [adminMenu, setAdminMenu] = useState([]);
  const [editingPromoId, setEditingPromoId] = useState(null); // null | "new" | <id>
  const [promoForm, setPromoForm] = useState(EMPTY_PROMO_FORM);
  const [editingMenuId, setEditingMenuId] = useState(null);
  const [menuForm, setMenuForm] = useState(EMPTY_MENU_FORM);

  const loadContent = useCallback(async () => {
    try {
      const [pr, mn] = await Promise.all([
        apiFetch("/admin/promos").then((r) => (r.ok ? r.json() : [])),
        apiFetch("/admin/menu").then((r) => (r.ok ? r.json() : [])),
      ]);
      setAdminPromos(Array.isArray(pr) ? pr : []);
      setAdminMenu(Array.isArray(mn) ? mn : []);
    } catch {
      setAdminPromos([]);
      setAdminMenu([]);
    }
  }, []);

  useEffect(() => {
    if (authed === true && tab === "content") loadContent();
  }, [authed, tab, loadContent]);

  const startEditPromo = (p) => {
    setEditingPromoId(p.id);
    setPromoForm({ emoji: p.emoji, title_id: p.title_id, title_en: p.title_en, desc_id: p.desc_id, desc_en: p.desc_en, active: p.active, order: p.order });
  };
  const startNewPromo = () => {
    setEditingPromoId("new");
    const nextOrder = adminPromos.length ? Math.max(...adminPromos.map((p) => p.order)) + 1 : 0;
    setPromoForm({ ...EMPTY_PROMO_FORM, order: nextOrder });
  };
  const cancelPromoEdit = () => {
    setEditingPromoId(null);
    setPromoForm(EMPTY_PROMO_FORM);
  };
  const savePromo = async () => {
    const isNew = editingPromoId === "new";
    const url = isNew ? "/admin/promos" : `/admin/promos/${editingPromoId}`;
    const r = await apiFetch(url, { method: isNew ? "POST" : "PUT", body: JSON.stringify(promoForm) });
    if (r.ok) {
      cancelPromoEdit();
      loadContent();
    }
  };
  const deletePromo = async (id) => {
    if (!window.confirm(T.confirmDelete)) return;
    await apiFetch(`/admin/promos/${id}`, { method: "DELETE" });
    loadContent();
  };

  const startEditMenu = (m) => {
    setEditingMenuId(m.id);
    setMenuForm({ category: m.category, name: m.name, price: m.price, desc_id: m.desc_id, desc_en: m.desc_en, active: m.active, order: m.order });
  };
  const startNewMenu = () => {
    setEditingMenuId("new");
    const sameCategory = adminMenu.filter((m) => m.category === EMPTY_MENU_FORM.category);
    const nextOrder = sameCategory.length ? Math.max(...sameCategory.map((m) => m.order)) + 1 : 0;
    setMenuForm({ ...EMPTY_MENU_FORM, order: nextOrder });
  };
  const cancelMenuEdit = () => {
    setEditingMenuId(null);
    setMenuForm(EMPTY_MENU_FORM);
  };
  const saveMenuItem = async () => {
    const isNew = editingMenuId === "new";
    const url = isNew ? "/admin/menu" : `/admin/menu/${editingMenuId}`;
    const body = { ...menuForm, price: Number(menuForm.price) || 0, order: Number(menuForm.order) || 0 };
    const r = await apiFetch(url, { method: isNew ? "POST" : "PUT", body: JSON.stringify(body) });
    if (r.ok) {
      cancelMenuEdit();
      loadContent();
    }
  };
  const deleteMenuItem = async (id) => {
    if (!window.confirm(T.confirmDelete)) return;
    await apiFetch(`/admin/menu/${id}`, { method: "DELETE" });
    loadContent();
  };

  // ===== Checking =====
  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[14px] text-[#6B7B8D]" data-testid="staff-checking">
        {T.staffChecking}
      </div>
    );
  }

  // ===== Gate =====
  if (authed === false) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen flex flex-col px-7 py-8 bg-gradient-to-b from-white to-[#F0F7FF]"
        data-testid="staff-gate"
      >
        <div className="flex justify-end">
          <LangToggle lang={lang} setLang={setLang} variant="dark" />
        </div>
        <div className="flex-1 flex flex-col justify-center max-w-[380px] w-full mx-auto">
          <div className="flex flex-col items-center text-center mb-7">
            <div className="w-16 h-16 rounded-full bg-[#1A3A5C] flex items-center justify-center mb-4">
              <Lock size={28} className="text-white" />
            </div>
            <h1 className="font-playfair font-bold text-[24px] text-[#1A1A2E]">{T.staffGateTitle}</h1>
            <p className="text-[14px] text-[#6B7B8D] mt-1.5">{T.staffGateSubtitle}</p>
          </div>

          <form onSubmit={unlock} className="space-y-4">
            <div className="relative">
              <KeyRound size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7B8D]" />
              <input
                data-testid="staff-passcode-input"
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder={T.staffGatePlaceholder}
                className="w-full bg-white border border-[#E0E8F0] rounded-[12px] h-[52px] pl-12 pr-4 text-[16px] text-[#1A1A2E] placeholder-[#6B7B8D] transition-all duration-200 focus:outline-none focus:border-[#4AADDF] focus:ring-[3px] focus:ring-[#4AADDF]/20"
              />
            </div>
            {gateError && <p className="text-[13px] text-[#E4572E] text-center" data-testid="staff-gate-error">{gateError}</p>}
            <button
              data-testid="staff-unlock-button"
              type="submit"
              disabled={!passcode || gateLoading}
              style={{ opacity: !passcode ? 0.5 : 1 }}
              className="w-full h-[52px] rounded-[12px] bg-gradient-to-r from-[#1A3A5C] to-[#367ABD] text-white font-bold text-[16px] shadow-[0_6px_18px_rgba(54,122,189,0.3)] transition-transform duration-200 active:scale-[0.98] disabled:cursor-not-allowed"
            >
              {T.staffGateBtn}
            </button>
          </form>
        </div>
      </motion.div>
    );
  }

  // ===== Dashboard =====
  const avg = feedback.length
    ? (feedback.reduce((s, f) => s + (f.rating || 0), 0) / feedback.length).toFixed(1)
    : "-";
  const pending = requests.filter((r) => !r.resolved).length;

  return (
    <div className="min-h-screen bg-[#F7FAFC]" data-testid="staff-screen">
      <div className="bg-gradient-to-r from-[#1A3A5C] to-[#367ABD] text-white pt-safe px-5 pb-6 rounded-b-[24px] shadow-lg relative">
        <div className="absolute right-4 top-safe z-20 flex items-center gap-2">
          <LangToggle lang={lang} setLang={setLang} variant="light" />
          <button
            data-testid="staff-logout-button"
            onClick={logout}
            className="flex items-center gap-1.5 text-[12px] font-medium bg-white/20 px-3 py-1.5 rounded-full active:scale-95 transition-transform"
          >
            <Lock size={13} /> {T.staffLogout}
          </button>
        </div>
        <h1 className="font-playfair font-bold text-[24px] pt-8">{T.staffTitle}</h1>
        <p className="text-[13px] text-white/85 mt-0.5">{T.staffSubtitle}</p>

        <div className="flex gap-3 mt-4">
          <div className="flex-1 bg-white/15 backdrop-blur rounded-[12px] px-4 py-3">
            <div className="text-[11px] text-white/80">{T.staffAvg}</div>
            <div className="text-[20px] font-bold flex items-center gap-1">
              {avg} <StarIcon size={16} fill="#F5E6B8" className="text-[#F5E6B8]" />
            </div>
          </div>
          <div className="flex-1 bg-white/15 backdrop-blur rounded-[12px] px-4 py-3">
            <div className="text-[11px] text-white/80">{T.staffTotal} {T.staffFeedbackTab}</div>
            <div className="text-[20px] font-bold">{feedback.length}</div>
          </div>
          <div className="flex-1 bg-white/15 backdrop-blur rounded-[12px] px-4 py-3">
            <div className="text-[11px] text-white/80">{T.staffPending}</div>
            <div className="text-[20px] font-bold flex items-center gap-1.5" data-testid="staff-pending-count">
              {pending}
              {pending > 0 && <span className="w-2 h-2 rounded-full bg-[#C9A84C]" />}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 px-4 py-3 sticky top-0 bg-[#F7FAFC] z-10 overflow-x-auto hide-scrollbar">
        <button
          data-testid="staff-tab-feedback"
          onClick={() => setTab("feedback")}
          className={`shrink-0 flex items-center gap-2 text-[14px] font-medium px-4 py-2 rounded-full transition-all ${
            tab === "feedback" ? "bg-[#4AADDF] text-white shadow-[0_4px_12px_rgba(74,173,223,0.3)]" : "bg-white text-[#6B7B8D] border border-[#E0E8F0]"
          }`}
        >
          <Star size={16} /> {T.staffFeedbackTab}
        </button>
        <button
          data-testid="staff-tab-requests"
          onClick={() => setTab("requests")}
          className={`shrink-0 relative flex items-center gap-2 text-[14px] font-medium px-4 py-2 rounded-full transition-all ${
            tab === "requests" ? "bg-[#4AADDF] text-white shadow-[0_4px_12px_rgba(74,173,223,0.3)]" : "bg-white text-[#6B7B8D] border border-[#E0E8F0]"
          }`}
        >
          <MessageSquare size={16} /> {T.staffRequestsTab}
          {pending > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-[#C9A84C] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
              {pending}
            </span>
          )}
        </button>
        <button
          data-testid="staff-tab-content"
          onClick={() => setTab("content")}
          className={`shrink-0 flex items-center gap-2 text-[14px] font-medium px-4 py-2 rounded-full transition-all ${
            tab === "content" ? "bg-[#4AADDF] text-white shadow-[0_4px_12px_rgba(74,173,223,0.3)]" : "bg-white text-[#6B7B8D] border border-[#E0E8F0]"
          }`}
        >
          <Settings size={16} /> {T.staffContentTab}
        </button>
        {tab === "requests" && (
          <button
            data-testid="staff-unresolved-toggle"
            onClick={() => setShowUnresolvedOnly((v) => !v)}
            className={`ml-auto flex items-center gap-1.5 text-[12px] font-medium px-3 py-2 rounded-full border transition-all active:scale-95 ${
              showUnresolvedOnly
                ? "bg-[#1A3A5C] text-white border-[#1A3A5C]"
                : "bg-white text-[#6B7B8D] border-[#E0E8F0]"
            }`}
          >
            <Filter size={14} /> {T.staffUnresolvedOnly}
          </button>
        )}
        <button
          data-testid="staff-refresh"
          onClick={load}
          className={`${tab === "requests" ? "" : "ml-auto "}w-9 h-9 flex items-center justify-center rounded-full bg-white border border-[#E0E8F0] text-[#4AADDF] active:scale-90 transition-transform`}
          aria-label={T.staffRefresh}
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="px-4 pb-10 space-y-3">
        {tab === "feedback" &&
          (feedback.length === 0 ? (
            <p className="text-center text-[14px] text-[#6B7B8D] py-16">{T.staffEmptyFeedback}</p>
          ) : (
            feedback.map((f, i) => (
              <motion.div
                key={f.id || i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="bg-white rounded-[14px] p-4 shadow-[0_2px_12px_rgba(74,173,223,0.08)] border border-[#F0F7FF]"
                data-testid="staff-feedback-card"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-[15px] text-[#1A1A2E]">{f.guest_name}</div>
                    <div className="text-[12px] text-[#6B7B8D]">{T.staffRoom} {f.room_number}</div>
                  </div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} size={15} fill={s <= f.rating ? "#C9A84C" : "none"} className={s <= f.rating ? "text-[#C9A84C]" : "text-[#D9E2EC]"} />
                    ))}
                  </div>
                </div>
                {f.comment ? <p className="text-[13px] text-[#1A1A2E] mt-2 leading-relaxed">"{f.comment}"</p> : null}
                <div className="text-[11px] text-[#A9B8C6] mt-2">{fmtDate(f.timestamp)}</div>
              </motion.div>
            ))
          ))}

        {tab === "requests" &&
          (threads.length === 0 ? (
            <p className="text-center text-[14px] text-[#6B7B8D] py-16">
              {requests.length === 0 ? T.staffEmptyRequests : T.staffEmptyUnresolved}
            </p>
          ) : (
            threads.map((t, i) => {
              const isOpen = expandedSessionId === t.sessionId;
              const isResolved = t.unresolvedCount === 0;
              return (
                <motion.div
                  key={t.sessionId}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className={`rounded-[14px] p-4 shadow-[0_2px_12px_rgba(74,173,223,0.08)] border-l-[3px] transition-colors ${
                    isResolved ? "bg-[#F4F7FA] border-[#2EAF7D]" : "bg-white border-[#C9A84C]"
                  }`}
                  data-testid="operator-thread-card"
                >
                  <div className="flex justify-between items-center gap-2">
                    <div className="font-semibold text-[14px] text-[#1A1A2E] flex items-center gap-2">
                      {!isResolved && (
                        <span data-testid="request-new-badge" className="bg-[#F5E6B8] text-[#8A6D1F] text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide">
                          {T.reqNew}
                        </span>
                      )}
                      <span>{t.guestName} <span className="text-[#6B7B8D] font-normal">· {T.staffRoom} {t.roomNumber}</span></span>
                    </div>
                    <div className="text-[11px] text-[#A9B8C6] whitespace-nowrap">{fmtDate(t.latest.timestamp)}</div>
                  </div>
                  <p className="text-[14px] text-[#1A1A2E] mt-1.5 leading-relaxed">{t.latest.text}</p>

                  <div className="flex gap-2 mt-3">
                    <button
                      data-testid="operator-thread-toggle"
                      onClick={() => toggleThread(t)}
                      className="inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-[10px] bg-[#4AADDF] text-white shadow-[0_3px_10px_rgba(74,173,223,0.3)] active:scale-95 transition-all"
                    >
                      <Send size={14} /> {isOpen ? T.reqCloseThread : T.reqReplyThread}
                    </button>
                    <button
                      data-testid="operator-thread-resolve"
                      onClick={() => setThreadResolved(t, !isResolved)}
                      className={`inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 py-2 rounded-[10px] transition-all active:scale-95 ${
                        isResolved
                          ? "bg-transparent text-[#2EAF7D] border border-[#2EAF7D]/40"
                          : "bg-[#2EAF7D] text-white shadow-[0_3px_10px_rgba(46,175,125,0.3)]"
                      }`}
                    >
                      <Check size={15} /> {isResolved ? T.reqReopen : T.reqMarkDone}
                    </button>
                  </div>
                  {isResolved && t.latest.resolved_by && (
                    <p className="text-[11px] text-[#A9B8C6] mt-2" data-testid="request-resolved-meta">
                      {T.reqResolvedBy(t.latest.resolved_by)} · {fmtDate(t.latest.resolved_at)}
                    </p>
                  )}

                  {isOpen && (
                    <div className="mt-3 pt-3 border-t border-[#F0F7FF]" data-testid="operator-thread-panel">
                      {threadLoading ? (
                        <p className="text-[13px] text-[#6B7B8D] text-center py-4">{T.staffChecking}</p>
                      ) : (
                        <div className="flex flex-col gap-2.5 max-h-[320px] overflow-y-auto hide-scrollbar pb-1" data-testid="operator-thread-messages">
                          {threadMessages.map((m, mi) => (
                            <ChatBubble key={m.id || mi} role={m.role === "user" ? "user" : "operator"} text={m.text} timestamp={fmtTime(m.timestamp)} />
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2.5">
                        <input
                          data-testid="operator-reply-input"
                          value={replyInput}
                          onChange={(e) => setReplyInput(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && sendReply(t)}
                          placeholder={T.reqReplyPlaceholder}
                          className="flex-1 bg-[#F0F7FF] border border-[#E0E8F0] rounded-full h-[42px] px-4 text-[14px] text-[#1A1A2E] placeholder-[#6B7B8D] focus:outline-none focus:border-[#4AADDF] focus:ring-[3px] focus:ring-[#4AADDF]/15 transition-all"
                        />
                        <button
                          data-testid="operator-reply-send"
                          onClick={() => sendReply(t)}
                          disabled={!replyInput.trim() || replySending}
                          className="w-[42px] h-[42px] rounded-full bg-[#4AADDF] text-white flex items-center justify-center shrink-0 active:scale-90 transition-transform disabled:opacity-50"
                          aria-label={T.reqReplyThread}
                        >
                          <Send size={17} />
                        </button>
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })
          ))}

        {tab === "content" && (
          <div data-testid="staff-content-panel">
            <div className="flex gap-2 mb-4">
              <button
                data-testid="content-subtab-promo"
                onClick={() => setContentTab("promo")}
                className={`text-[13px] font-medium px-3.5 py-1.5 rounded-full transition-all ${
                  contentTab === "promo" ? "bg-[#1A3A5C] text-white" : "bg-white text-[#6B7B8D] border border-[#E0E8F0]"
                }`}
              >
                {T.contentPromoSubtab}
              </button>
              <button
                data-testid="content-subtab-menu"
                onClick={() => setContentTab("menu")}
                className={`text-[13px] font-medium px-3.5 py-1.5 rounded-full transition-all ${
                  contentTab === "menu" ? "bg-[#1A3A5C] text-white" : "bg-white text-[#6B7B8D] border border-[#E0E8F0]"
                }`}
              >
                {T.contentMenuSubtab}
              </button>
            </div>

            {contentTab === "promo" && (
              <div className="space-y-3">
                {editingPromoId === "new" ? (
                  <div className="bg-white rounded-[14px] p-4 border border-[#4AADDF] space-y-2" data-testid="promo-form">
                    <input data-testid="promo-form-emoji" value={promoForm.emoji} onChange={(e) => setPromoForm({ ...promoForm, emoji: e.target.value })} placeholder={T.fieldEmoji} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <input data-testid="promo-form-title-id" value={promoForm.title_id} onChange={(e) => setPromoForm({ ...promoForm, title_id: e.target.value })} placeholder={T.fieldTitleId} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <input data-testid="promo-form-title-en" value={promoForm.title_en} onChange={(e) => setPromoForm({ ...promoForm, title_en: e.target.value })} placeholder={T.fieldTitleEn} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <textarea data-testid="promo-form-desc-id" value={promoForm.desc_id} onChange={(e) => setPromoForm({ ...promoForm, desc_id: e.target.value })} placeholder={T.fieldDescId} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <textarea data-testid="promo-form-desc-en" value={promoForm.desc_en} onChange={(e) => setPromoForm({ ...promoForm, desc_en: e.target.value })} placeholder={T.fieldDescEn} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <label className="flex items-center gap-2 text-[13px] text-[#1A1A2E]">
                      <input type="checkbox" checked={promoForm.active} onChange={(e) => setPromoForm({ ...promoForm, active: e.target.checked })} /> {T.fieldActive}
                    </label>
                    <div className="flex gap-2 pt-1">
                      <button data-testid="promo-form-save" onClick={savePromo} className="flex-1 h-[38px] rounded-[8px] bg-[#2EAF7D] text-white text-[13px] font-semibold">{T.save}</button>
                      <button onClick={cancelPromoEdit} className="w-[38px] h-[38px] rounded-[8px] bg-[#F0F7FF] flex items-center justify-center"><X size={16} /></button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="add-promo-button" onClick={startNewPromo} className="w-full h-[42px] rounded-[10px] border border-dashed border-[#4AADDF] text-[#4AADDF] text-[13px] font-medium flex items-center justify-center gap-1.5">
                    <Plus size={15} /> {T.addPromo}
                  </button>
                )}

                {adminPromos.map((p) =>
                  editingPromoId === p.id ? (
                    <div key={p.id} className="bg-white rounded-[14px] p-4 border border-[#4AADDF] space-y-2" data-testid="promo-form">
                      <input data-testid="promo-form-emoji" value={promoForm.emoji} onChange={(e) => setPromoForm({ ...promoForm, emoji: e.target.value })} placeholder={T.fieldEmoji} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <input data-testid="promo-form-title-id" value={promoForm.title_id} onChange={(e) => setPromoForm({ ...promoForm, title_id: e.target.value })} placeholder={T.fieldTitleId} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <input data-testid="promo-form-title-en" value={promoForm.title_en} onChange={(e) => setPromoForm({ ...promoForm, title_en: e.target.value })} placeholder={T.fieldTitleEn} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <textarea data-testid="promo-form-desc-id" value={promoForm.desc_id} onChange={(e) => setPromoForm({ ...promoForm, desc_id: e.target.value })} placeholder={T.fieldDescId} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <textarea data-testid="promo-form-desc-en" value={promoForm.desc_en} onChange={(e) => setPromoForm({ ...promoForm, desc_en: e.target.value })} placeholder={T.fieldDescEn} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <label className="flex items-center gap-2 text-[13px] text-[#1A1A2E]">
                        <input type="checkbox" checked={promoForm.active} onChange={(e) => setPromoForm({ ...promoForm, active: e.target.checked })} /> {T.fieldActive}
                      </label>
                      <div className="flex gap-2 pt-1">
                        <button data-testid="promo-form-save" onClick={savePromo} className="flex-1 h-[38px] rounded-[8px] bg-[#2EAF7D] text-white text-[13px] font-semibold">{T.save}</button>
                        <button onClick={cancelPromoEdit} className="w-[38px] h-[38px] rounded-[8px] bg-[#F0F7FF] flex items-center justify-center"><X size={16} /></button>
                      </div>
                    </div>
                  ) : (
                    <div key={p.id} className="bg-white rounded-[14px] p-4 border border-[#F0F7FF] shadow-[0_2px_12px_rgba(74,173,223,0.08)]" data-testid="promo-admin-card">
                      <div className="flex justify-between items-start gap-2">
                        <h3 className="font-inter font-semibold text-[14px] text-[#1A1A2E]">{p.emoji} {p.title_id}</h3>
                        {!p.active && <span className="text-[10px] font-bold text-[#A9B8C6] border border-[#E0E8F0] rounded-full px-2 py-0.5 shrink-0">{T.fieldInactive}</span>}
                      </div>
                      <p className="text-[13px] text-[#6B7B8D] mt-1">{p.desc_id}</p>
                      <div className="flex gap-2 mt-3">
                        <button data-testid="promo-edit-button" onClick={() => startEditPromo(p)} className="flex items-center gap-1 text-[12px] font-medium text-[#4AADDF] border border-[#4AADDF]/40 rounded-[8px] px-3 py-1.5"><Pencil size={13} /> {T.edit}</button>
                        <button data-testid="promo-delete-button" onClick={() => deletePromo(p.id)} className="flex items-center gap-1 text-[12px] font-medium text-[#E4572E] border border-[#E4572E]/40 rounded-[8px] px-3 py-1.5"><Trash2 size={13} /> {T.delete}</button>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            {contentTab === "menu" && (
              <div className="space-y-3">
                {editingMenuId === "new" ? (
                  <div className="bg-white rounded-[14px] p-4 border border-[#4AADDF] space-y-2" data-testid="menu-form">
                    <select data-testid="menu-form-category" value={menuForm.category} onChange={(e) => setMenuForm({ ...menuForm, category: e.target.value })} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]">
                      <option value="food">{T.catFood}</option>
                      <option value="drink">{T.catDrink}</option>
                      <option value="snack">{T.catSnack}</option>
                    </select>
                    <input data-testid="menu-form-name" value={menuForm.name} onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })} placeholder={T.fieldName} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <input data-testid="menu-form-price" type="number" value={menuForm.price} onChange={(e) => setMenuForm({ ...menuForm, price: e.target.value })} placeholder={T.fieldPrice} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <textarea data-testid="menu-form-desc-id" value={menuForm.desc_id} onChange={(e) => setMenuForm({ ...menuForm, desc_id: e.target.value })} placeholder={T.fieldDescId} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <textarea data-testid="menu-form-desc-en" value={menuForm.desc_en} onChange={(e) => setMenuForm({ ...menuForm, desc_en: e.target.value })} placeholder={T.fieldDescEn} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                    <label className="flex items-center gap-2 text-[13px] text-[#1A1A2E]">
                      <input type="checkbox" checked={menuForm.active} onChange={(e) => setMenuForm({ ...menuForm, active: e.target.checked })} /> {T.fieldActive}
                    </label>
                    <div className="flex gap-2 pt-1">
                      <button data-testid="menu-form-save" onClick={saveMenuItem} className="flex-1 h-[38px] rounded-[8px] bg-[#2EAF7D] text-white text-[13px] font-semibold">{T.save}</button>
                      <button onClick={cancelMenuEdit} className="w-[38px] h-[38px] rounded-[8px] bg-[#F0F7FF] flex items-center justify-center"><X size={16} /></button>
                    </div>
                  </div>
                ) : (
                  <button data-testid="add-menu-button" onClick={startNewMenu} className="w-full h-[42px] rounded-[10px] border border-dashed border-[#4AADDF] text-[#4AADDF] text-[13px] font-medium flex items-center justify-center gap-1.5">
                    <Plus size={15} /> {T.addMenuItem}
                  </button>
                )}

                {adminMenu.map((m) =>
                  editingMenuId === m.id ? (
                    <div key={m.id} className="bg-white rounded-[14px] p-4 border border-[#4AADDF] space-y-2" data-testid="menu-form">
                      <select data-testid="menu-form-category" value={menuForm.category} onChange={(e) => setMenuForm({ ...menuForm, category: e.target.value })} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]">
                        <option value="food">{T.catFood}</option>
                        <option value="drink">{T.catDrink}</option>
                        <option value="snack">{T.catSnack}</option>
                      </select>
                      <input data-testid="menu-form-name" value={menuForm.name} onChange={(e) => setMenuForm({ ...menuForm, name: e.target.value })} placeholder={T.fieldName} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <input data-testid="menu-form-price" type="number" value={menuForm.price} onChange={(e) => setMenuForm({ ...menuForm, price: e.target.value })} placeholder={T.fieldPrice} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <textarea data-testid="menu-form-desc-id" value={menuForm.desc_id} onChange={(e) => setMenuForm({ ...menuForm, desc_id: e.target.value })} placeholder={T.fieldDescId} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <textarea data-testid="menu-form-desc-en" value={menuForm.desc_en} onChange={(e) => setMenuForm({ ...menuForm, desc_en: e.target.value })} placeholder={T.fieldDescEn} rows={2} className="w-full border border-[#E0E8F0] rounded-[8px] px-3 py-2 text-[14px]" />
                      <label className="flex items-center gap-2 text-[13px] text-[#1A1A2E]">
                        <input type="checkbox" checked={menuForm.active} onChange={(e) => setMenuForm({ ...menuForm, active: e.target.checked })} /> {T.fieldActive}
                      </label>
                      <div className="flex gap-2 pt-1">
                        <button data-testid="menu-form-save" onClick={saveMenuItem} className="flex-1 h-[38px] rounded-[8px] bg-[#2EAF7D] text-white text-[13px] font-semibold">{T.save}</button>
                        <button onClick={cancelMenuEdit} className="w-[38px] h-[38px] rounded-[8px] bg-[#F0F7FF] flex items-center justify-center"><X size={16} /></button>
                      </div>
                    </div>
                  ) : (
                    <div key={m.id} className="bg-white rounded-[14px] p-4 border border-[#F0F7FF] shadow-[0_2px_12px_rgba(74,173,223,0.08)]" data-testid="menu-admin-card">
                      <div className="flex justify-between items-start gap-2">
                        <h3 className="font-inter font-semibold text-[14px] text-[#1A1A2E]">{m.name}</h3>
                        <span className="font-inter font-bold text-[13px] text-[#4AADDF] whitespace-nowrap">{formatRupiah(m.price)}</span>
                      </div>
                      <p className="text-[12px] text-[#A9B8C6] mt-0.5">{m.category}{!m.active ? ` · ${T.fieldInactive}` : ""}</p>
                      <p className="text-[13px] text-[#6B7B8D] mt-1">{m.desc_id}</p>
                      <div className="flex gap-2 mt-3">
                        <button data-testid="menu-edit-button" onClick={() => startEditMenu(m)} className="flex items-center gap-1 text-[12px] font-medium text-[#4AADDF] border border-[#4AADDF]/40 rounded-[8px] px-3 py-1.5"><Pencil size={13} /> {T.edit}</button>
                        <button data-testid="menu-delete-button" onClick={() => deleteMenuItem(m.id)} className="flex items-center gap-1 text-[12px] font-medium text-[#E4572E] border border-[#E4572E]/40 rounded-[8px] px-3 py-1.5"><Trash2 size={13} /> {T.delete}</button>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
